// features/admin/warehouseApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export type LocationType = 'warehouse' | 'shop' | 'home';

export const LOCATION_TYPE_OPTIONS: { value: LocationType; label: string }[] = [
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'shop', label: 'Shop' },
  { value: 'home', label: 'Home' },
];

export interface Warehouse {
  id: number;
  vendor_id?: number | null;
  vendor_first_name?: string;
  vendor_last_name?: string;
  name: string;
  latitude: string;
  longitude: string;
  address?: string;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  location_type?: LocationType;
  status: number;
  product_id?: number;
  is_default?: number; // 0 or 1
  created_at?: string;
  updated_at?: string;
}

export interface WarehouseFormData {
  name: string;
  latitude: string;
  longitude: string;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  location_type?: LocationType;
  status: number;
  product_id?: number;
  id?: number;
  is_default?: number; // 0 or 1
}

export interface WarehouseResponse {
  error: boolean;
  message: string;
  warehouses?: Warehouse[];
  result?: Warehouse[];
  id?: number; // in add API response
}

export interface WarehouseRequest {
  name: string;
  latitude: string;
  longitude: string;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  location_type?: LocationType;
  status: number;
  product_id?: number;
  is_default?: number; // 0 or 1
}

export interface UpdateWarehouseRequest extends WarehouseRequest {
  id: number;
}

export interface DeleteWarehouseRequest {
  id: number;
}

// Status options
export const STATUS_OPTIONS = [
  { value: 1, label: 'Active' },
  { value: 2, label: 'Inactive' },
];

// Coordinated Validation helper
export const validateCoordinate = (value: string, type: 'latitude' | 'longitude'): boolean => {
  const num = parseFloat(value);
  if (isNaN(num)) return false;
  
  if (type === 'latitude') {
    return num >= -90 && num <= 90;
  } else {
    return num >= -180 && num <= 180;
  }
};

export const warehouseApi = createApi({
  reducerPath: 'warehouseApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['Warehouse'],
  endpoints: (build) => ({
    listWarehouses: build.query<WarehouseResponse, void>({
      query: () => ({
        url: 'product/warehouselist',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['Warehouse'],

      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        warehouses: response.warehouses || response.result || [],
        id: response.id,
      }),
    }),

    addWarehouse: build.mutation<WarehouseResponse, WarehouseRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('name', data.name.trim());
        formData.append('latitude', data.latitude.trim());
        formData.append('longitude', data.longitude.trim());
        formData.append('status', data.status.toString());
        formData.append('location_type', data.location_type || 'warehouse');

        if (data.street?.trim()) formData.append('street', data.street.trim());
        if (data.city?.trim()) formData.append('city', data.city.trim());
        if (data.state?.trim()) formData.append('state', data.state.trim());
        if (data.postal_code?.trim()) formData.append('postal_code', data.postal_code.trim());

        if (typeof data.product_id !== 'undefined') {
          formData.append('product_id', data.product_id.toString());
        }

        if (typeof data.is_default !== 'undefined') {
          formData.append('is_default', data.is_default.toString());
        }

        return {
          url: 'product/warehouseadd',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Warehouse'],
    }),

    updateWarehouse: build.mutation<WarehouseResponse, UpdateWarehouseRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('name', data.name.trim());
        formData.append('latitude', data.latitude.trim());
        formData.append('longitude', data.longitude.trim());
        formData.append('status', data.status.toString());

        if (data.location_type) {
          formData.append('location_type', data.location_type);
        }

        if (typeof data.street !== 'undefined') formData.append('street', data.street?.trim() || '');
        if (typeof data.city !== 'undefined') formData.append('city', data.city?.trim() || '');
        if (typeof data.state !== 'undefined') formData.append('state', data.state?.trim() || '');
        if (typeof data.postal_code !== 'undefined') formData.append('postal_code', data.postal_code?.trim() || '');

        if (typeof data.product_id !== 'undefined') {
          formData.append('product_id', data.product_id.toString());
        }

        if (typeof data.is_default !== 'undefined') {
          formData.append('is_default', data.is_default.toString());
        }

        return {
          url: 'product/warehouseupdate',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Warehouse'],
    }),

    deleteWarehouse: build.mutation<WarehouseResponse, DeleteWarehouseRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());

        return {
          url: 'product/warehousedelete',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Warehouse'],
    }),
  }),
});

export const {
  useListWarehousesQuery,
  useAddWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
} = warehouseApi;